import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { ArrowLeft, Trash2, Upload, RefreshCw, Save, Loader2, Home } from "lucide-react";

const SAMPLE_JSON = `[
  {
    "name": "Serenity", "base_price": 305000, "beds": 4, "baths": 2,
    "living_areas": 2, "storeys": 1, "garage": 2, "house_size_m2": 210,
    "frontage_m": 14, "alfresco": true, "study": true, "multigen": false,
    "family_profiles": ["Popular"], "image_url": ""
  }
]`;

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("library");
  const [floorplans, setFloorplans] = useState([]);
  const [assumptions, setAssumptions] = useState(null);
  const [importText, setImportText] = useState(SAMPLE_JSON);
  const [importFmt, setImportFmt] = useState("json");
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.floorplans().then(setFloorplans).catch(() => {});
    api.getAssumptions().then(setAssumptions).catch(() => {});
  };
  useEffect(load, []);

  const doImport = async () => {
    setBusy(true);
    try {
      const r = await api.importFloorplans({ format: importFmt, content: importText, replace });
      toast.success(`Imported ${r.imported} floorplans.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed.");
    } finally { setBusy(false); }
  };

  const reseed = async () => {
    setBusy(true);
    try { const r = await api.reseed(); toast.success(`Reseeded ${r.seeded}.`); load(); }
    catch { toast.error("Reseed failed."); } finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.deleteFloorplan(id); setFloorplans((f) => f.filter((x) => x.id !== id)); }
    catch { toast.error("Delete failed."); }
  };

  const saveAssumptions = async () => {
    setBusy(true);
    try { const r = await api.putAssumptions(assumptions); setAssumptions(r); toast.success("Finance assumptions saved."); }
    catch { toast.error("Save failed."); } finally { setBusy(false); }
  };

  const setA = (k, v) => setAssumptions((a) => ({ ...a, [k]: v }));

  return (
    <div className="min-h-screen bg-bg relative">
      <div className="ambient-glow" />
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between">
          <button data-testid="admin-back-btn" onClick={() => navigate("/")} className="hotspot flex items-center gap-2 text-mutedText hover:text-forest text-sm">
            <ArrowLeft size={16} /> Back to experience
          </button>
          <div className="flex items-center gap-2 text-forest"><Home size={16} className="text-brand" /><span className="label-eyebrow text-forest/70">Admin · Stage 1</span></div>
        </div>

        <h1 className="font-serif text-5xl font-light tracking-tight text-forest mt-6">Builder Console</h1>

        <div className="mt-8 flex gap-2 border-b border-line">
          {[["library", "Floorplan Library"], ["import", "Import"], ["finance", "Finance Assumptions"]].map(([k, l]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-3 text-sm transition border-b-2 -mb-px ${tab === k ? "border-brand text-forest font-medium" : "border-transparent text-mutedText"}`}>{l}</button>
          ))}
        </div>

        {tab === "library" && (
          <div className="mt-6" data-testid="admin-library">
            <div className="flex items-center justify-between mb-4">
              <p className="text-mutedText text-sm">{floorplans.length} floorplans in library</p>
              <button data-testid="admin-reseed-btn" onClick={reseed} disabled={busy}
                className="flex items-center gap-2 rounded-full border border-line px-4 py-2 text-sm text-forest hover:bg-surface">
                <RefreshCw size={14} /> Reseed samples
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {floorplans.map((fp) => (
                <div key={fp.id} data-testid={`admin-fp-${fp.name}`} className="rounded-2xl border border-line bg-white/5 overflow-hidden">
                  <div className="h-32 bg-surface overflow-hidden">{fp.image_url && <img src={fp.image_url} alt={fp.name} className="h-full w-full object-cover" />}</div>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-serif text-2xl text-forest">{fp.name}</h3>
                      <button data-testid={`admin-delete-${fp.name}`} onClick={() => del(fp.id)} className="text-mutedText hover:text-brand"><Trash2 size={16} /></button>
                    </div>
                    <p className="text-sm text-mutedText mt-1">{fp.beds} bed · {fp.baths} bath · {fp.storeys} storey · {fp.house_size_m2}m²</p>
                    <p className="text-brand font-medium mt-1">{money(fp.base_price)}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(fp.family_profiles || []).map((t) => <span key={t} className="rounded-full bg-surface px-2 py-0.5 text-[11px] text-forest/70">{t}</span>)}
                      <span className="rounded-full bg-white/5 px-2 py-0.5 text-[11px] text-mutedText">{fp.source}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "import" && (
          <div className="mt-6 max-w-3xl" data-testid="admin-import">
            <p className="text-mutedText text-sm mb-4">Import your tagged floorplan export (structured JSON or CSV). This is the source of truth — scraping is only a fallback seed.</p>
            <div className="flex items-center gap-3 mb-3">
              <select data-testid="admin-import-format" value={importFmt} onChange={(e) => setImportFmt(e.target.value)}
                className="rounded-lg border border-line bg-white/5 px-3 py-2 text-sm">
                <option value="json">JSON</option><option value="csv">CSV</option>
              </select>
              <label className="flex items-center gap-2 text-sm text-forest">
                <input data-testid="admin-import-replace" type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
                Replace existing library
              </label>
            </div>
            <textarea data-testid="admin-import-text" value={importText} onChange={(e) => setImportText(e.target.value)} rows={12}
              className="w-full rounded-xl border border-line bg-white/5 p-4 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-brand" />
            <button data-testid="admin-import-btn" onClick={doImport} disabled={busy}
              className="mt-4 flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-white text-sm font-medium hover:bg-brand disabled:opacity-50">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Import floorplans
            </button>
            <p className="text-xs text-mutedText mt-3">CSV columns: name, base_price, beds, baths, living_areas, storeys, garage, house_size_m2, frontage_m, alfresco, study, theatre, scullery, activity_room, multigen, family_profiles (pipe|separated), image_url</p>
          </div>
        )}

        {tab === "finance" && assumptions && (
          <div className="mt-6 max-w-2xl" data-testid="admin-finance">
            <div className="grid grid-cols-2 gap-5">
              {[
                ["interest_rate", "Product rate %"], ["assessment_buffer", "Assessment buffer %"],
                ["loan_term_years", "Loan term (yrs)"], ["max_lvr", "Max LVR %"],
                ["deposit_buffer", "Deposit buffer $"], ["base_living_monthly", "Base living $/mo"],
                ["couple_uplift_monthly", "Couple uplift $/mo"], ["per_dependant_monthly", "Per dependant $/mo"],
                ["default_inclusions", "Default inclusions $"],
              ].map(([k, l]) => (
                <div key={k}>
                  <label className="label-eyebrow text-mutedText">{l}</label>
                  <input data-testid={`admin-assumption-${k}`} type="number" value={assumptions[k]} onChange={(e) => setA(k, Number(e.target.value))}
                    className="mt-1.5 w-full rounded-lg border border-line bg-white/5 px-3 py-2.5 text-forest focus:outline-none focus:ring-2 focus:ring-brand" />
                </div>
              ))}
            </div>

            <h3 className="font-serif text-2xl text-forest mt-8 mb-3">State allowances</h3>
            <div className="space-y-2">
              {Object.entries(assumptions.states || {}).map(([code, s]) => (
                <div key={code} className="grid grid-cols-5 gap-2 items-center rounded-lg border border-line bg-white/5 p-2 text-sm">
                  <span className="font-medium text-forest">{code}</span>
                  <label className="text-xs text-mutedText">Duty %<input type="number" step="0.001" value={s.stamp_duty_rate}
                    onChange={(e) => setAssumptions((a) => ({ ...a, states: { ...a.states, [code]: { ...s, stamp_duty_rate: Number(e.target.value) } } }))}
                    className="mt-1 w-full rounded border border-line px-2 py-1" /></label>
                  <label className="text-xs text-mutedText">FHB concession<input type="number" value={s.first_home_concession}
                    onChange={(e) => setAssumptions((a) => ({ ...a, states: { ...a.states, [code]: { ...s, first_home_concession: Number(e.target.value) } } }))}
                    className="mt-1 w-full rounded border border-line px-2 py-1" /></label>
                  <label className="text-xs text-mutedText">Site costs<input type="number" value={s.site_costs}
                    onChange={(e) => setAssumptions((a) => ({ ...a, states: { ...a.states, [code]: { ...s, site_costs: Number(e.target.value) } } }))}
                    className="mt-1 w-full rounded border border-line px-2 py-1" /></label>
                  <label className="text-xs text-mutedText">Grant<input type="number" value={s.grant}
                    onChange={(e) => setAssumptions((a) => ({ ...a, states: { ...a.states, [code]: { ...s, grant: Number(e.target.value) } } }))}
                    className="mt-1 w-full rounded border border-line px-2 py-1" /></label>
                </div>
              ))}
            </div>

            <button data-testid="admin-save-assumptions-btn" onClick={saveAssumptions} disabled={busy}
              className="mt-6 flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-white text-sm font-medium hover:bg-brand-dark disabled:opacity-50">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save assumptions
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
